// The alignment editor: the heading bar, the menu bar, the toolbar, the two columns and the
// status bar over one open session, with the command palette and the shortcuts sheet over them.
// Every command is in the registry (commands.ts), which the keymap, the menus, the palette, the
// toolbar and the sheet read; this file gives the commands the editor they act on. Every change
// goes to the Worker as an action, and its answer patches the rows the page holds.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { caretIn, placeCaret } from '../core/caret.ts'
import { ShellContext } from '../../app/hooks.ts'
import { AppMenuBar } from '../../app/MenuBar.tsx'
import { Palette } from '../../app/Palette.tsx'
import type { Platform } from '../../core/bindings.ts'
import { displayChord } from '../../core/keymap.ts'
import { download, SESSION_SUFFIX, writeToFolder } from '../core/files.ts'
import { languageName } from '../core/languages.ts'
import { FONT_SIZES, getPref, setPref } from '../core/prefs.ts'
import { Rows } from '../core/rows.ts'
import { cellOf, type Column, type Direction, type RowView } from '../core/types.ts'
import type { AutosaveNotice, Engine } from '../engine/client.ts'
import type { Action, Done, State } from '../engine/protocol.ts'
import { commands, TOOLBAR, type Aligner } from './commands.ts'
import { Grid, type Cells, type Cursor, type Editing } from './Grid.tsx'
import { AlignBar } from './Heading.tsx'
import { AlignShortcuts } from './Sheet.tsx'
import { useDocumentKeys } from './keys.ts'
import { Shell } from './shell.ts'
import { desktop, OPENING, type SaveKind } from './desktop.ts'
import { findKey, openKeys, pickKey, placeOf, send } from './review.ts'
import { ReviewSheet, type ReviewState } from './ReviewSheet.tsx'
import { memoryOrganisations, openMemories, sendToMemory } from './memory.ts'
import { MemorySheet, type MemoryState } from './MemorySheet.tsx'
import { sourceName as memorySource, type Organisation } from '../core/memory.ts'
import { signInLink } from '../core/review.ts'
import type { ProviderKey } from '../../core/keys.ts'

interface Props {
  engine: Engine
  initial: State
  folder: FileSystemDirectoryHandle | null
  autosave: AutosaveNotice | null
  onClose: () => void
}

interface Said {
  text: string
  alert?: boolean
}

interface Report {
  file: string
  paired: number
  oneSided: number
  excluded: number
  where: string
}

/// What the busy line says for the operations that can take a second or more.
const SLOW: Partial<Record<Action['kind'], string>> = {
  lockRealign: 'Locking and realigning…',
  realign: 'Realigning…',
  pinpoint: 'Pinpointing and realigning…',
}

const other = (c: Column): Column => (c === 'Source' ? 'Target' : 'Source')

/// The one registry: every command is defined there once. In the desktop app the system draws the
/// menus (brief 46).
const REGISTRY = commands(desktop ? 'system' : 'page')

export function Editor({ engine, initial, folder, autosave, onClose }: Props) {
  const [state, setState] = useState(initial)
  const [version, setVersion] = useState(0)
  const rows = useMemo(() => {
    const r = new Rows(engine, () => setVersion(v => v + 1))
    r.reset(initial.len)
    return r
  }, [engine, initial])
  const [cursor, setCursorState] = useState<Cursor>({ row: 0, column: 'Source' })
  const [caret, setCaret] = useState(false)
  const [editing, setEditing] = useState<Editing | null>(null)
  /// The other end of a run of selected cells in the cursor's column, null for the cursor's
  /// cell alone. Shift with Up, Down or a click makes one.
  const [anchor, setAnchor] = useState<number | null>(null)
  const [pin, setPin] = useState<Cells | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const working = useRef(false)
  const [said, setSaid] = useState<Said | null>(null)
  const [report, setReport] = useState<Report | null>(null)
  const [nonce, setNonce] = useState(0)
  const [scrollTo, setScrollTo] = useState({ row: 0, seq: 0 })
  const [fonts, setFonts] = useState(() => ({
    Source: getPref(`font.${initial.languages[0]}`, initial.languages[0].startsWith('th') ? 16 : 14),
    Target: getPref(`font.${initial.languages[1]}`, initial.languages[1].startsWith('th') ? 16 : 14),
  }))
  const [divider, setDivider] = useState(() => getPref('divider', 0.5))
  /// When the last change was applied, to tell a change the autosave has not caught up with.
  const [changedAt, setChangedAt] = useState(0)
  /// Review with Claude's sheet, from the first check to what it did (brief 48).
  const [review, setReview] = useState<ReviewState | null>(null)
  const stopping = useRef(false)
  /// Send to memory… (brief 49): the organisations whose memory the person may send to, read
  /// once the editor opens, and the sheet.
  const [organisations, setOrganisations] = useState<Organisation[]>([])
  const [memory, setMemory] = useState<MemoryState | null>(null)
  useEffect(() => {
    let live = true
    void memoryOrganisations().then(o => live && setOrganisations(o))
    return () => void (live = false)
  }, [])

  const setCursor = useCallback((next: Cursor, scroll = true) => {
    setCursorState(next)
    if (scroll) setScrollTo(s => ({ row: next.row, seq: s.seq + 1 }))
  }, [])
  const current = rows.get(cursor.row)
  /// The cells commands act on: the selected run, or the cursor's cell.
  const span: Cells = anchor === null
    ? { column: cursor.column, first: cursor.row, last: cursor.row }
    : { column: cursor.column, first: Math.min(anchor, cursor.row), last: Math.max(anchor, cursor.row) }
  const several = span.last > span.first

  const cellElement = (c: Cursor) => document.querySelector<HTMLElement>(`.cell[data-row="${c.row}"][data-column="${c.column}"]`)
  const leaveCaret = () => {
    setCaret(false)
    getSelection()?.removeAllRanges()
    document.querySelector<HTMLElement>('[data-grid]')?.focus({ preventScroll: true })
  }

  /// Sends one action and applies its answer. Actions wait for the one before to finish.
  const run = async (action: Action, follow?: (done: Done, before: RowView | undefined) => number | null) => {
    if (working.current) return
    working.current = true
    const slow = SLOW[action.kind]
    const timer = slow ? setTimeout(() => setBusy(slow), 150) : null
    const before = rows.get(cursor.row)
    try {
      const done = await engine.request('do', { action })
      // The follow runs after the selection is cleared, since a move sets it again.
      applied(done, () => follow?.(done, before) ?? cursor.row)
      setSaid(done.change ? null : { text: 'Nothing changed.' })
    } catch (e) {
      setSaid({ text: (e as Error).message, alert: true })
    } finally {
      if (timer) clearTimeout(timer)
      setBusy(null)
      working.current = false
    }
  }

  /// An answer's rows patched into the page's, and the cursor put on the row `row` gives.
  const applied = (done: Done, row: () => number) => {
    if (done.change) {
      rows.splice(done.change, done.rows, done.state.len)
      setChangedAt(Date.now())
    }
    setState(done.state)
    // Every cell is drawn afresh, so a caret in one is gone.
    setNonce(n => n + 1)
    setCaret(false)
    setAnchor(null)
    setCursor({ row: Math.max(0, Math.min(row(), done.state.len - 1)), column: cursor.column })
  }

  // Review with Claude (brief 48): where it runs, the key, the plan and its cost, then the windows
  // one at a time until they are done or Cancel stops it after the one in flight.
  const startReview = async () => {
    setReview({ stage: 'checking' })
    const place = await placeOf()
    if (place.kind === 'elsewhere') return setReview({ stage: 'elsewhere', link: signInLink(place) })
    let found: Awaited<ReturnType<typeof findKey>>
    try {
      found = await findKey()
    } catch (e) {
      setReview(null)
      return setSaid({ text: `Reading your keys: ${(e as Error).message}`, alert: true })
    }
    if (found.kind === 'none') return setReview({ stage: 'no-key' })
    if (found.kind === 'choose') return setReview({ stage: 'choose', keys: found.keys })
    await planReview(found.key)
  }
  const planReview = async (key: ProviderKey) => {
    try {
      const { estimate } = await engine.request('reviewPlan', { endpoint: key.endpoint })
      if (estimate.windows === 0) {
        await engine.request('reviewDrop', {})
        return setReview({ stage: 'nothing' })
      }
      setReview({ stage: 'confirm', key, estimate })
    } catch (e) {
      setReview(null)
      setSaid({ text: `Planning the review: ${(e as Error).message}`, alert: true })
    }
  }
  const closeReview = () => {
    if (review?.stage === 'running' || review?.stage === 'checking') return
    if (review?.stage === 'confirm') void engine.request('reviewDrop', {})
    setReview(null)
    document.querySelector<HTMLElement>('[data-grid]')?.focus({ preventScroll: true })
  }
  const runReview = async (key: ProviderKey, total: number) => {
    if (working.current) return
    working.current = true
    stopping.current = false
    setBusy('Reviewing with Claude…')
    setReview({ stage: 'running', key, progress: { done: 0, total, failed: 0 }, stopping: false })
    try {
      for (;;) {
        if (stopping.current) break
        const request = await engine.request('reviewNext', {})
        if (!request) break
        const response = await send(key, request)
        const heard = await engine.request('reviewAnswer', { response })
        setReview(r => (r?.stage === 'running' ? { ...r, progress: heard.progress } : r))
        if (heard.wait_ms && !stopping.current) await new Promise(r => setTimeout(r, heard.wait_ms))
      }
      const { done, review: result } = await engine.request('reviewFinish', {})
      applied(done, () => cursor.row)
      setReview({ stage: 'done', review: result, cancelled: stopping.current })
    } catch (e) {
      setReview(null)
      setSaid({ text: `Reviewing: ${(e as Error).message}`, alert: true })
    } finally {
      setBusy(null)
      working.current = false
    }
  }
  const stopReview = () => {
    stopping.current = true
    setReview(r => (r?.stage === 'running' ? { ...r, stopping: true } : r))
  }

  const startMemory = () => {
    if (organisations.length > 1) return setMemory({ stage: 'choose', organisations })
    if (organisations[0]) void confirmMemory(organisations[0])
  }
  const confirmMemory = async (organisation: Organisation) => {
    try {
      const tmx = await engine.request('export', {})
      setMemory({ stage: 'confirm', organisation, name: memorySource(state.opened), xml: tmx.xml, paired: tmx.paired, oneSided: tmx.one_sided, excluded: state.counts.excluded })
    } catch (e) {
      setMemory(null)
      setSaid({ text: `Exporting: ${(e as Error).message}`, alert: true })
    }
  }
  const sendMemory = async () => {
    if (memory?.stage !== 'confirm') return
    const { organisation, name, xml } = memory
    setMemory({ stage: 'sending', organisation, name })
    try {
      const { imported, source } = await sendToMemory(organisation, name, xml)
      setMemory({ stage: 'done', organisation, name, imported, source })
    } catch (e) {
      setMemory(null)
      setSaid({ text: `Sending to ${organisation.name}’s memory: ${(e as Error).message}`, alert: true })
    }
  }
  const closeMemory = () => {
    if (memory?.stage === 'sending') return
    setMemory(null)
    document.querySelector<HTMLElement>('[data-grid]')?.focus({ preventScroll: true })
  }

  /// After an operation that moves the cursor's row, the row with the same texts that the
  /// operation locked, if one is among the new rows.
  const lockedRow = (done: Done, texts: [string, string] | null): number | null => {
    if (!done.change || !texts) return null
    const k = done.rows.findIndex(r => r.locked && textOf(r, 'Source') === texts[0] && textOf(r, 'Target') === texts[1])
    return k < 0 ? null : done.change.at + k
  }

  const split = () => {
    const el = cellElement(cursor)
    const at = caret && el ? caretIn(el) : null
    if (!at) return setSaid({ text: `Put a caret in the cell first: press Return, move it with the arrows, then ${shell.chord('row.split')}.` })
    void run({ kind: 'split', row: cursor.row, column: cursor.column, piece: at.piece, offset: at.offset })
  }

  /// Moves the cursor's cell, or the selected run, a row up or down, and the cursor and the
  /// selection go with it, so the same key moves it again.
  const move = (direction: Direction) => {
    const { first, last } = span
    const atBottom = several && cursor.row === last
    void run({ kind: 'move', column: cursor.column, first, last, direction }, done => {
      if (!done.moved) return null
      if (several) setAnchor(atBottom ? done.moved.first : done.moved.last)
      return atBottom ? done.moved.last : done.moved.first
    })
  }

  const join = () => {
    if (several) void run({ kind: 'joinCells', column: span.column, first: span.first, last: span.last }, () => span.first)
    else void run({ kind: 'join', row: cursor.row, column: cursor.column })
  }

  const startEdit = () => {
    if (!current) return
    const cell = cellOf(current, cursor.column)
    if (!cell.pieces.length) return setSaid({ text: 'The cell is blank: there is no text to edit.' })
    setCaret(false)
    setEditing({ ...cursor, text: cell.pieces.map(p => p.text).join('\n') })
  }

  const editingRef = useRef(editing)
  editingRef.current = editing
  const finishEdit = (confirm: boolean) => {
    const e = editingRef.current
    if (!e) return
    editingRef.current = null
    setEditing(null)
    document.querySelector<HTMLElement>('[data-grid]')?.focus({ preventScroll: true })
    if (confirm) void run({ kind: 'edit', row: e.row, column: e.column, text: e.text })
  }

  const pinpoint = () => {
    if (!pin || pin.column === cursor.column) {
      setPin(span)
      const which = several ? 'the first selected cell goes beside' : 'belongs with this one'
      setSaid({ text: `Pinpoint Align: go to the ${other(cursor.column).toLowerCase()} cell that ${which} and press Return, or click it. Escape cancels.` })
      return
    }
    const top = rows.get(pin.first), partner = rows.get(cursor.row)
    const texts: [string, string] | null = top && partner
      ? pin.column === 'Source' ? [textOf(top, 'Source'), textOf(partner, 'Target')] : [textOf(partner, 'Source'), textOf(top, 'Target')]
      : null
    setPin(null)
    void run({ kind: 'pinpoint', column: pin.column, first: pin.first, last: pin.last, row: cursor.row }, done => lockedRow(done, texts))
  }

  const nextFlagged = async () => {
    let row = await engine.request('nextFlagged', { after: cursor.row })
    if (row === null) row = await engine.request('nextFlagged', { after: null })
    if (row === null) return setSaid({ text: 'No row is flagged.' })
    if (row <= cursor.row) setSaid({ text: 'Back at the first flagged row.' })
    else setSaid(null)
    leaveCaret()
    setAnchor(null)
    setCursor({ row, column: cursor.column })
  }

  const where = folder ? `the folder ${folder.name}` : desktop ? 'the place you choose' : 'your downloads'
  /// A file written to the folder, the desktop app's save panel or the downloads. Answers where it
  /// went, or null when the panel was cancelled.
  const put = async (file: string, text: string, type: string, kind: SaveKind): Promise<string | null> => {
    if (folder) return (await writeToFolder(folder, file, text), where)
    if (desktop) {
      const path = await desktop.save(file, text, kind)
      return path && (path.split(/[\\/]/).slice(0, -1).pop() ?? path)
    }
    download(file, text, type)
    return where
  }
  const save = async () => {
    try {
      const json = await engine.request('save', {})
      const file = `${state.name}${SESSION_SUFFIX}`
      const to = await put(file, json, 'application/json', 'alignSession')
      if (to !== null) setSaid({ text: `Saved ${file} to ${to}.` })
    } catch (e) {
      setSaid({ text: `Saving: ${(e as Error).message}`, alert: true })
    }
  }

  const exportTmx = async () => {
    try {
      const tmx = await engine.request('export', {})
      const file = `${state.name}.tmx`
      const to = await put(file, tmx.xml, 'application/x-tmx+xml', 'tmx')
      if (to !== null) setReport({ file, paired: tmx.paired, oneSided: tmx.one_sided, excluded: state.counts.excluded, where: to })
    } catch (e) {
      setSaid({ text: `Exporting: ${(e as Error).message}`, alert: true })
    }
  }

  const font = (column: Column, step: number) => {
    const k = FONT_SIZES.indexOf(fonts[column])
    const size = FONT_SIZES[Math.max(0, Math.min(FONT_SIZES.length - 1, (k < 0 ? 3 : k) + step))]!
    setFonts(f => ({ ...f, [column]: size }))
    setPref(`font.${state.languages[column === 'Source' ? 0 : 1]}`, size)
  }

  const cell = current ? cellOf(current, cursor.column) : null
  const topView = rows.get(span.first)
  const aligner: Aligner = {
    idle: !busy && review === null && memory === null,
    has: current !== undefined,
    text: !!cell && cell.pieces.length > 0,
    several,
    topText: !!topView && cellOf(topView, span.column).pieces.length > 0,
    edited: !!cell?.edited,
    locked: !!current?.locked,
    excluded: !!current?.excluded,
    canUndo: state.canUndo,
    canRedo: state.canRedo,
    equalColumns: divider === 0.5,
    where,
    open: onClose,
    save: () => void save(),
    exportTmx: () => void exportTmx(),
    undo: () => void run({ kind: 'undo' }, d => d.change?.at ?? null),
    redo: () => void run({ kind: 'redo' }, d => d.change?.at ?? null),
    edit: startEdit,
    revert: () => void run({ kind: 'edit', row: cursor.row, column: cursor.column, text: null }),
    split,
    join,
    move,
    lockRealign: () => void run({ kind: 'lockRealign', row: cursor.row }, done => lockedRow(done, current ? [textOf(current, 'Source'), textOf(current, 'Target')] : null)),
    lock: () => void run({ kind: 'lock', row: cursor.row, locked: !current?.locked }),
    exclude: () => void run({ kind: 'exclude', row: cursor.row, excluded: !current?.excluded }),
    pinpoint,
    realign: () => void run({ kind: 'realign' }),
    nextFlagged: () => void nextFlagged(),
    review: () => void startReview(),
    memory: organisations.length > 0,
    sendToMemory: startMemory,
    font,
    equal: () => (setDivider(0.5), setPref('divider', 0.5)),
    say: text => setSaid({ text }),
  }
  const shellRef = useRef<Shell | null>(null)
  shellRef.current ??= new Shell(REGISTRY, aligner)
  const shell = shellRef.current
  shell.a = aligner
  useDocumentKeys(shell)
  // In the desktop app the system's menu bar is this editor's while the window is key: its model
  // after every render, and a chosen item runs here (brief 46). The open screen's come back after.
  useEffect(() => {
    if (!desktop) return
    desktop.model = () => shell.menuModel()
    desktop.run = id => void shell.command(id)
    desktop.schedule()
  })
  useEffect(() => () => {
    if (!desktop) return
    desktop.model = () => OPENING
    desktop.run = () => {}
    desktop.schedule()
  }, [])

  // The grid's own keys, which no menu lists: moving between cells, Tab to the other column,
  // Return for a caret and Escape to take it out. They act while the focus is in the grid, or on
  // nothing at all. Escape with nothing else to cancel leaves the grid: Tab then moves through the
  // page until the focus comes back to the grid or an arrow key moves the cursor.
  const released = useRef(false)
  const reviewRef = useRef(review)
  reviewRef.current = review
  const closeRef = useRef(closeReview)
  closeRef.current = closeReview
  const memoryRef = useRef(memory)
  memoryRef.current = memory
  const closeMemoryRef = useRef(closeMemory)
  closeMemoryRef.current = closeMemory
  const keys = useRef<(e: KeyboardEvent) => void>(() => {})
  keys.current = (e: KeyboardEvent) => {
    if (report) {
      if (e.key === 'Escape' || e.key === 'Enter') (e.preventDefault(), setReport(null))
      return
    }
    if (reviewRef.current) {
      if (e.key === 'Escape') (e.preventDefault(), closeRef.current())
      return
    }
    if (memoryRef.current) {
      if (e.key === 'Escape') (e.preventDefault(), closeMemoryRef.current())
      return
    }
    const target = e.target as HTMLElement | null
    const grid = document.querySelector<HTMLElement>('[data-grid]')
    if (editing || e.defaultPrevented || !(target === document.body || (grid && target && grid.contains(target)))) return
    if (shell.state.palette || shell.state.menu || shell.state.shortcuts) return
    if (e.metaKey || e.ctrlKey || e.altKey) return
    const len = state.len
    // Shift with a key that moves the cursor up or down extends the selection in the column.
    const go = (row: number, column = cursor.column) => {
      e.preventDefault()
      released.current = false
      leaveCaret()
      const extend = e.shiftKey && column === cursor.column
      setAnchor(extend ? (anchor ?? cursor.row) : null)
      setCursor({ row: Math.max(0, Math.min(len - 1, row)), column })
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      if (pin) (setPin(null), setSaid({ text: 'Pinpoint Align cancelled.' }))
      else if (caret) leaveCaret()
      else if (anchor !== null) setAnchor(null)
      else if (target !== document.body) {
        released.current = true
        setSaid({ text: 'Out of the grid: Tab moves through the page. An arrow key comes back.' })
      }
    } else if (e.key === 'Enter') {
      e.preventDefault()
      released.current = false
      if (pin && pin.column !== cursor.column) return pinpoint()
      if (caret) return
      const el = cellElement(cursor)
      if (el?.isContentEditable) {
        setCaret(true)
        placeCaret(el)
      }
    } else if (e.key === 'Tab') {
      if (released.current || target === document.body) return
      go(cursor.row, other(cursor.column))
    } else if (caret) return
    else if (e.key === 'ArrowDown') go(cursor.row + 1)
    else if (e.key === 'ArrowUp') go(cursor.row - 1)
    else if (e.key === 'ArrowLeft') go(cursor.row, 'Source')
    else if (e.key === 'ArrowRight') go(cursor.row, 'Target')
    else if (e.key === 'PageDown') go(cursor.row + 10)
    else if (e.key === 'PageUp') go(cursor.row - 10)
    else if (e.key === 'Home') go(0)
    else if (e.key === 'End') go(len - 1)
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keys.current(e)
    // The focus coming into the grid from elsewhere takes it back into the grid's keys.
    const onFocus = (e: FocusEvent) => {
      const grid = document.querySelector('[data-grid]')
      if (grid && e.target instanceof Node && grid.contains(e.target) && !(e.relatedTarget instanceof Node && grid.contains(e.relatedTarget))) released.current = false
    }
    window.addEventListener('keydown', onKey)
    document.addEventListener('focusin', onFocus)
    document.querySelector<HTMLElement>('[data-grid]')?.focus({ preventScroll: true })
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('focusin', onFocus)
    }
  }, [])

  // The column divider: dragged in the header, kept as a share of the width (decision 14).
  const frame = useRef<HTMLDivElement>(null)
  const drag = (e: React.PointerEvent) => {
    e.preventDefault()
    const box = frame.current?.getBoundingClientRect()
    if (!box) return
    const handle = e.currentTarget as HTMLElement
    handle.setPointerCapture(e.pointerId)
    let share = divider
    const moved = (m: PointerEvent) => {
      share = Math.max(0.2, Math.min(0.8, (m.clientX - box.left - 48) / (box.width - 48 - 12 - 40)))
      setDivider(share)
    }
    const up = () => {
      handle.removeEventListener('pointermove', moved)
      handle.removeEventListener('pointerup', up)
      setPref('divider', share)
    }
    handle.addEventListener('pointermove', moved)
    handle.addEventListener('pointerup', up)
  }

  const pick = (next: Cursor, withCaret: boolean, extend: boolean) => {
    if (editing) return
    if (pin && pin.column !== next.column && withCaret) {
      setCursorState(next)
      setTimeout(() => shell.a.pinpoint())
      return
    }
    if (extend && next.column === cursor.column) {
      leaveCaret()
      setAnchor(anchor ?? cursor.row)
      setCursor(next, false)
      return
    }
    setAnchor(null)
    setCursor(next, false)
    setCaret(withCaret)
  }

  // A test hook, as Interplae's gates read `interplae`: the state and the rows the page holds.
  useEffect(() => {
    ;(window as unknown as { align: unknown }).align = {
      state, cursor, caret, busy, editing, pin, said, anchor, review, memory, organisations,
      working: () => working.current,
      released: () => released.current,
      shell: () => shell.state,
      /// Every command's id and its chord as each platform writes it, from the registry.
      commands: () => shell.registry.all().map(c => {
        const on = (p: Platform) => {
          const chord = shell.registry.chord(c.id, p)
          return chord && displayChord(chord, p)
        }
        return { id: c.id, mac: on('mac'), windows: on('windows') }
      }),
      row: (k: number) => rows.get(k),
      texts: () => Array.from({ length: rows.length }, (_, k) => {
        const r = rows.get(k)
        return r ? [textOf(r, 'Source'), textOf(r, 'Target')] : null
      }),
    }
  })

  const style = {
    '--cols': `48px minmax(0, ${divider}fr) 12px minmax(0, ${1 - divider}fr) 40px`,
    '--font-source': `${fonts.Source}px`,
    '--font-target': `${fonts.Target}px`,
  } as React.CSSProperties
  const [sourceName, targetName] = state.languages.map(languageName) as [string, string]
  const saving = autosave !== null && 'savedAt' in autosave && changedAt > autosave.savedAt

  return (
    <ShellContext.Provider value={shell}>
      <div className="workspace" ref={frame} style={style}>
        <AlignBar opened={state.opened} autosave={autosave} saving={saving} onHome={onClose} />
        <AppMenuBar />
        <Toolbar shell={shell} pin={pin !== null} />
        <div className="columns">
          <div className="colhead">
            <div />
            <ColumnHead name={sourceName} side="Source" size={fonts.Source} onFont={step => font('Source', step)} />
            <div className="handle" onPointerDown={drag} title="Drag to resize the columns" role="separator" aria-orientation="vertical" />
            <ColumnHead name={targetName} side="Target" size={fonts.Target} onFont={step => font('Target', step)} />
            <div />
          </div>
          <Grid
            rows={rows}
            version={version}
            len={state.len}
            notesStart={state.notesStart}
            cursor={cursor}
            selection={several ? span : null}
            caret={caret}
            editing={editing}
            pin={pin}
            languages={state.languages}
            nonce={nonce}
            scrollTo={scrollTo}
            onPick={pick}
            onEditText={text => setEditing(e => (e ? { ...e, text } : e))}
            onEditDone={finishEdit}
            onTyped={() => setNonce(n => n + 1)}
          />
        </div>
        <div className="statusbar">
          <span className="place">{several ? `Rows ${span.first + 1}–${span.last + 1}` : `Row ${Math.min(cursor.row + 1, state.len)}`} of {state.len.toLocaleString()}</span>
          {busy ? (
            <span className="busy" role="status">
              {busy}
              <span className="busy-bar indeterminate"><span /></span>
            </span>
          ) : said ? (
            <span className={`message${said.alert ? ' alert' : ''}`} role="status">{said.text}</span>
          ) : (
            <span className="message muted">{caret ? `Caret in the cell: ${shell.shownChord('row.split')} splits here; Escape leaves it.` : 'Return puts a caret in the cell.'}</span>
          )}
          <span className="right">
            <span data-counts>
              {state.counts.flagged} flagged · {state.counts.one_sided} one-sided · {state.counts.excluded} excluded · {state.counts.locked} locked
            </span>
            {folder && <span title="Saves and exports go to this folder">{folder.name}</span>}
            <button className="sbtn shortcuts" data-command="shortcuts.open" onClick={() => shell.command('shortcuts.open')}>Shortcuts</button>
          </span>
        </div>
        {report && (
          <div className="dialog" onMouseDown={e => { if (e.target === e.currentTarget) setReport(null) }}>
            <div className="dbox report" role="dialog" aria-label="Exported">
              <h2>Exported {report.file}</h2>
              <p data-report="paired">{plural(report.paired, 'pair')} written to {report.where}.</p>
              <p data-report="left-out">
                {report.oneSided === 0 ? 'No one-sided rows were left out.' : `${plural(report.oneSided, 'one-sided row')} left out: a row needs text on both sides to go into a translation memory.`}
                {report.excluded > 0 && ` ${plural(report.excluded, 'excluded row')} left out as marked.`}
              </p>
              <div className="dactions">
                <button className="primary" autoFocus onClick={() => setReport(null)}>OK</button>
              </div>
            </div>
          </div>
        )}
        {review && (
          <ReviewSheet
            state={review}
            onClose={closeReview}
            onSave={() => void save()}
            onKeys={() => (openKeys(), closeReview())}
            onChoose={key => (pickKey(key), void planReview(key))}
            onReview={() => review.stage === 'confirm' && void runReview(review.key, review.estimate.windows)}
            onStop={stopReview}
          />
        )}
        {memory && (
          <MemorySheet
            state={memory}
            onClose={closeMemory}
            onChoose={o => void confirmMemory(o)}
            onSend={() => void sendMemory()}
            onOpen={() => memory.stage === 'done' && (openMemories(memory.source), closeMemory())}
          />
        )}
        <Palette heading="In this session" />
        <AlignShortcuts />
      </div>
    </ShellContext.Provider>
  )
}

/// The most-used actions (the aligner's handoff, "Next"), in the shell's toolbar: each button runs
/// its command from the registry, disabled where the command's `when` does not hold, and its
/// tooltip gives the command's title and chord. Export, the one action that leaves the page, is at
/// the end, as the shell puts its layouts.
function Toolbar({ shell, pin }: { shell: Shell; pin: boolean }) {
  const button = (id: string, label: string, className = '') => {
    const command = shell.registry.get(id)
    if (!command) return null
    const enabled = !command.when || command.when(shell)
    const chord = shell.shownChord(id)
    return (
      <button
        key={id}
        className={`tool ${className}`}
        disabled={!enabled}
        data-command={id}
        title={chord ? `${command.title} (${chord})` : command.title}
        onMouseDown={e => e.preventDefault()}
        onClick={() => shell.command(id)}
      >
        {label}
      </button>
    )
  }
  return (
    <div className="toolbar" role="toolbar" aria-label="Actions">
      {TOOLBAR.map(group => (
        <span className="group" key={group[0]!.id}>
          {group.map(b => button(b.id, b.label, b.id === 'row.pinpoint' && pin ? 'on' : ''))}
        </span>
      ))}
      <span className="group end">{button('session.export', 'Export TMX')}</span>
    </div>
  )
}

function ColumnHead({ name, side, size, onFont }: { name: string; side: Column; size: number; onFont: (step: number) => void }) {
  return (
    <div className="colname" data-side={side}>
      <span>
        <b>{name}</b> <span className="muted">{side === 'Source' ? 'source' : 'target'}</span>
      </span>
      <span className="fontsize">
        <button className="tool" title={`Smaller ${side.toLowerCase()} text`} onMouseDown={e => e.preventDefault()} onClick={() => onFont(-1)} data-font={`${side}-`}>A−</button>
        <span className="muted" data-font-size={side}>{size}</span>
        <button className="tool" title={`Larger ${side.toLowerCase()} text`} onMouseDown={e => e.preventDefault()} onClick={() => onFont(1)} data-font={`${side}+`}>A+</button>
      </span>
    </div>
  )
}

const textOf = (row: RowView, column: Column) => cellOf(row, column).pieces.map(p => p.text).join('\n')

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`
